import React from 'react';
import { HiArrowDown, HiArrowUp, HiSelector } from 'react-icons/hi';
import type { CustomerSortBy, SortDir } from '../../api/customer';
import { customerInformationStrings as S } from './CustomerInformation.strings';

/**
 * Props of a sortable column header, generic over the list's sort columns.
 * `C` defaults to Customer Information's columns so existing references to
 * `SortableHeaderProps` keep their meaning.
 */
export type SortableHeaderProps<C extends string = CustomerSortBy> = {
  /** The column this header sorts by. */
  column: C;
  /** Visible column name. */
  label: string;
  /** Optional tooltip on the column name. */
  hint?: string;
  /** The list's active sort column; `null` means no column is active. */
  sortBy: C | null;
  sortDir: SortDir;
  onSort: (column: C) => void;
  /** Direction of the first click on an inactive column. Default `'desc'`. */
  firstDir?: SortDir;
  /** Words announced for each direction. Default Customer Information's `S.sort.ascending` / `S.sort.descending`. */
  directionLabels?: { asc: string; desc: string };
  /** Classes on the `<th>`. Default `'px-6 py-3'`. */
  className?: string;
};

type AriaSort = 'ascending' | 'descending' | 'none';

const DEFAULT_DIRECTION_LABELS = { asc: S.sort.ascending, desc: S.sort.descending };

/**
 * Column header that sorts a list on the server.
 *
 * A native `<button>` inside the `<th>`, so click, Enter and Space all sort
 * and the header is reachable by keyboard; `aria-sort` on the `<th>` announces
 * the current direction. The accessible name says what the next click does
 * (an inactive column starts in `firstDir`, the active one flips), mirroring
 * the list's toggle (e.g. `toggleSort` in `customerListQuery.ts`).
 *
 * A generic function component, because `React.FC` cannot take a type
 * parameter; call sites infer `C` from `column`, `sortBy` and `onSort`.
 */
function SortableHeader<C extends string>({
  column,
  label,
  hint,
  sortBy,
  sortDir,
  onSort,
  firstDir = 'desc',
  directionLabels = DEFAULT_DIRECTION_LABELS,
  className = 'px-6 py-3',
}: SortableHeaderProps<C>): React.ReactElement {
  const isActive = sortBy === column;
  const ariaSort: AriaSort = !isActive ? 'none' : sortDir === 'asc' ? 'ascending' : 'descending';
  const flipped: SortDir = sortDir === 'asc' ? 'desc' : 'asc';
  const nextDir: SortDir = isActive ? flipped : firstDir;
  const accessibleName = S.sort.buttonLabel
    .replace('{column}', label)
    .replace('{direction}', directionLabels[nextDir]);

  let icon: React.ReactNode;
  if (!isActive) {
    icon = <HiSelector className="h-4 w-4 text-gray-400" />;
  } else if (sortDir === 'asc') {
    icon = <HiArrowUp className="h-3.5 w-3.5 text-gray-900" />;
  } else {
    icon = <HiArrowDown className="h-3.5 w-3.5 text-gray-900" />;
  }

  return (
    <th scope="col" className={className} aria-sort={ariaSort}>
      <button
        type="button"
        aria-label={accessibleName}
        title={hint}
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 uppercase hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded ${
          isActive ? 'font-bold text-gray-900' : ''
        }`}
      >
        <span>{label}</span>
        <span aria-hidden="true" data-sort-icon={isActive ? sortDir : 'none'}>
          {icon}
        </span>
      </button>
    </th>
  );
}

export default SortableHeader;
